import { Link } from 'react-router-dom';
import type { OrderSummary } from '@harbor/shared';
import { useApi } from '../lib/hooks.js';
import { money, shortDate, titleCase, orderStatusTone } from '../lib/format.js';
import { Badge, Button, Card, EmptyState, Skeleton } from '../components/ui.js';

export function Orders() {
  const { data, loading, error, refetch } = useApi<{ orders: OrderSummary[] }>('/api/orders');

  if (loading && !data) {
    return (
      <div className="mx-auto max-w-4xl space-y-3 px-4 py-12 sm:px-6">
        <Skeleton className="h-8 w-48" />
        {[0, 1, 2].map((index) => (
          <Skeleton key={index} className="h-20 w-full rounded-xl" />
        ))}
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-4xl px-4 py-12 sm:px-6">
      <h1 className="text-2xl font-semibold tracking-tight">Order history</h1>
      <p className="mt-1.5 text-sm text-muted">
        Every order placed on this account. Guests do not appear here.
      </p>

      {error && (
        <Card className="mt-6 p-6">
          <p className="text-sm text-danger">{error.message}</p>
          <Button variant="secondary" size="sm" className="mt-3" onClick={refetch}>
            Try again
          </Button>
        </Card>
      )}

      {data && data.orders.length === 0 && !error && (
        <div className="mt-8">
          <EmptyState
            title="No orders yet"
            description="Once you place an order it will appear here with its full status history."
            action={
              <Link
                to="/"
                className="inline-flex h-11 items-center rounded-lg bg-accent px-4 text-sm font-medium text-accent-ink"
              >
                Browse the catalogue
              </Link>
            }
          />
        </div>
      )}

      {data && data.orders.length > 0 && (
        <ul className="mt-6 space-y-3">
          {data.orders.map((order) => {
            const tone = orderStatusTone(order.status);
            return (
              <li key={order.id}>
                <Link
                  to={`/orders/${order.id}`}
                  className="block rounded-xl border border-line bg-surface p-4 transition-colors hover:border-line-strong"
                >
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <p className="tabular text-sm font-semibold">{order.number}</p>
                      <p className="mt-0.5 text-xs text-subtle">
                        {shortDate(order.placedAt)} · {order.itemCount} item
                        {order.itemCount === 1 ? '' : 's'}
                      </p>
                    </div>
                    <div className="flex items-center gap-3">
                      <Badge tone={tone}>{titleCase(order.status)}</Badge>
                      <span className="tabular text-sm font-medium">
                        {money(order.totalMinor, order.currency)}
                      </span>
                      <ChevronIcon />
                    </div>
                  </div>
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function ChevronIcon() {
  return (
    <svg viewBox="0 0 24 24" className="size-4 text-subtle" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path d="m9 6 6 6-6 6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
